/**
 * 売上・原価の一括取り込み(2026-10-23 段階3「台帳」の型)。売上・原価入力の画面から開く。
 * 貼り付け(シートの表)か CSV ファイル → 内容を確かめる → 「新規」の行だけ登録する。判定は salesImport.ts
 */
import { useEffect, useMemo, useState } from 'react';
import { useAuth } from '@core/auth/AuthProvider';
import { useMasters } from '@core/masters/MastersProvider';
import { useToast } from '@core/components/Toast';
import Modal from '@core/components/Modal';
import { parseCsv, readCsvFile } from '@core/cash/csv';
import { fetchVendors } from '@core/masters/vendorRepo';
import { fetchPlTransactionsForMonths } from '@core/pl/accountPlRepo';
import { parsePastedTable, toTsv } from '@core/partners/partnerCsv';
import { formatYen } from '@core/lib/money';
import { toShortDate } from '@core/lib/date';
import type { DepartmentId, Transaction, Vendor } from '@core/lib/types';
import { createDirectEntries } from '@core/transactions/revenueRepo';
import { SALES_IMPORT_HEADERS, checkSalesImport, monthsOfTable, type SalesImportRow } from './salesImport';

const STATUS_LABEL: Record<SalesImportRow['status'], string> = { new: '新規', duplicate: '重複', error: 'エラー' };
const STATUS_BADGE: Record<SalesImportRow['status'], string> = { new: 'b-ok', duplicate: 'b-pend', error: 'b-ng' };

export default function SalesImportModal({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const { user } = useAuth();
  const { departments, accounts, subAccounts, companies, fiscal, departmentName, accountName } = useMasters();
  const toast = useToast();
  const [text, setText] = useState('');
  const [rows, setRows] = useState<SalesImportRow[] | null>(null);
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    fetchVendors(true)
      .then(setVendors)
      .catch(() => setVendors([]));
  }, []);

  async function readFile(file: File | undefined) {
    if (!file) return;
    try {
      setText(await readCsvFile(file));
      setRows(null);
    } catch {
      toast('ファイルを読めませんでした');
    }
  }

  async function copyHeaders() {
    try {
      await navigator.clipboard.writeText(toTsv([[...SALES_IMPORT_HEADERS]]));
      toast('見出しをコピーしました。シートの1行目に貼り付けてください');
    } catch {
      toast('コピーできませんでした');
    }
  }

  async function check() {
    if (!text.trim()) {
      toast('貼り付けるか、ファイルを選んでください');
      return;
    }
    const table = parsePastedTable(text, parseCsv);
    const months = monthsOfTable(table);
    setBusy(true);
    try {
      // 重複の判定のため、表に出てくる事業部×月の取引を読む(読めない事業部はエラーにする)
      const first = checkSalesImport({ table, masters: { departments, accounts, subAccounts, vendors, companies }, existing: [] });
      const deptIds = [...new Set(first.map((r) => r.input?.departmentId).filter((d): d is DepartmentId => !!d))];
      // 読めなかった事業部の行だけエラーにし、ほかの事業部は読めた取引で重複を確かめる
      const existing: Transaction[] = [];
      const unreadable = new Set<DepartmentId>();
      for (const d of deptIds) {
        try {
          existing.push(...(await fetchPlTransactionsForMonths(d, months)));
        } catch {
          unreadable.add(d);
        }
      }
      if (unreadable.size > 0) {
        toast(`${[...unreadable].map(departmentName).join('・')}の取引を読めないため、その事業部の行は登録できません`);
      }
      const checked = checkSalesImport({ table, masters: { departments, accounts, subAccounts, vendors, companies }, existing });
      setRows(
        checked.map((r) =>
          r.input && unreadable.has(r.input.departmentId)
            ? { ...r, status: 'error', message: '重複を確かめられない事業部です(この事業部の取引を見る権限がありません)', input: null }
            : r,
        ),
      );
    } finally {
      setBusy(false);
    }
  }

  const newRows = useMemo(() => (rows ?? []).filter((r) => r.status === 'new' && r.input), [rows]);
  const counts = useMemo(() => {
    const c = { new: 0, duplicate: 0, error: 0 };
    for (const r of rows ?? []) c[r.status]++;
    return c;
  }, [rows]);
  const newTotal = newRows.reduce((s, r) => s + (r.input?.amount ?? 0), 0);

  async function doImport() {
    if (!user || newRows.length === 0) return;
    setBusy(true);
    try {
      const result = await createDirectEntries({
        inputs: newRows.map((r) => r.input!),
        accounts,
        userId: user.id,
        fiscalStartMonth: fiscal.start_month,
        action: 'sales-import',
      });
      if (result.error) {
        toast(`${result.created}件を登録しました。残りは登録できませんでした: ${result.error}`);
        setRows(null);
      } else {
        toast(`${result.created}件を登録しました`);
        onDone();
        onClose();
      }
    } catch (e) {
      toast(e instanceof Error ? e.message : '取り込みに失敗しました');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal onClose={onClose} width="min(920px, 94vw)">
      <div style={{ display: 'flex', alignItems: 'flex-start', marginBottom: 12 }}>
        <h3>売上・原価の一括取り込み</h3>
      </div>
      <p className="hint" style={{ marginBottom: 10 }}>
        台帳(シート)を次の見出しの列に並べて貼り付けてください: {SALES_IMPORT_HEADERS.join('・')}。
        必須は 計上日・事業部・勘定科目・金額(税込)。事業部・科目・取引先は名前でもコードでも構いません。登録されるのは「新規」の行だけです。
      </p>
      <button className="txn-link" onClick={() => void copyHeaders()} style={{ marginBottom: 8 }}>
        見出しをコピー
      </button>
      <textarea
        value={text}
        onChange={(e) => {
          setText(e.target.value);
          setRows(null);
        }}
        placeholder="スプレッドシートの表(見出し行を含む)をここに貼り付け"
        rows={6}
        style={{ width: '100%', fontFamily: 'inherit', padding: 8, border: '1px solid var(--line)', borderRadius: 6 }}
      />
      <div style={{ marginTop: 8 }}>
        <label className="hint">または CSV ファイル(UTF-8・Shift_JIS)</label>
        <input type="file" accept=".csv,text/csv" onChange={(e) => void readFile(e.target.files?.[0])} />
      </div>

      {rows && (
        <div style={{ marginTop: 14 }}>
          <p style={{ margin: '0 0 8px' }}>
            新規 {counts.new}件(¥{formatYen(newTotal)})/ 重複 {counts.duplicate}件 / エラー {counts.error}件
          </p>
          <table>
            <thead>
              <tr>
                <th>行</th>
                <th>計上日</th>
                <th>事業部</th>
                <th>科目</th>
                <th>取引先</th>
                <th className="num">金額</th>
                <th>判定</th>
                <th>内容</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.line}>
                  <td className="hint">{r.line}</td>
                  <td>{r.input ? toShortDate(r.input.postingDate) : '—'}</td>
                  <td>{r.input ? departmentName(r.input.departmentId) : '—'}</td>
                  <td>{r.input ? accountName(r.input.accountId) : '—'}</td>
                  <td>{r.input?.counterparty || '—'}</td>
                  <td className="num">{r.input ? formatYen(r.input.amount) : '—'}</td>
                  <td>
                    <span className={`badge ${STATUS_BADGE[r.status]}`}>{STATUS_LABEL[r.status]}</span>
                  </td>
                  <td className="hint">{r.message}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="modal-actions">
        <button className="btn-line" onClick={() => void check()} disabled={busy}>
          内容を確かめる
        </button>
        <button className="btn-green" onClick={() => void doImport()} disabled={busy || newRows.length === 0}>
          {busy && rows ? '登録中…' : `新規 ${newRows.length}件を登録する`}
        </button>
      </div>
    </Modal>
  );
}
